// Customer Journey Service for Advanced User Behavior Tracking
// Tracks multi-session paths, returning vs new customers, geographic patterns

interface JourneySession {
  sessionId: string;
  timestamp: number;
  storeId: string;
  duration: number;
  interactions: {
    searches: string[];
    categories_viewed: string[];
    products_viewed: string[];
    cart_actions: number;
    filters_used: string[];
  };
  outcome: 'purchase' | 'abandoned' | 'ongoing';
  value: number;
}

interface CustomerProfile {
  customerId: string;
  firstVisit: number;
  totalSessions: number;
  totalPurchases: number;
  totalValue: number;
  preferredCategories: string[];
  preferredStores: string[];
  averageSessionDuration: number;
  lastVisit: number;
}

class CustomerJourneyService {
  private static instance: CustomerJourneyService;
  private currentSession: JourneySession | null = null;
  private customerId: string;

  private constructor() {
    this.customerId = this.getOrCreateCustomerId();
    this.initializeCurrentSession();
  }

  static getInstance(): CustomerJourneyService {
    if (!CustomerJourneyService.instance) {
      CustomerJourneyService.instance = new CustomerJourneyService();
    }
    return CustomerJourneyService.instance;
  }

  private getOrCreateCustomerId(): string {
    let customerId = localStorage.getItem('ss_customer_id');
    if (!customerId) {
      customerId = `customer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('ss_customer_id', customerId);
    }
    return customerId;
  }

  private initializeCurrentSession() {
    const sessionId = sessionStorage.getItem('ss_session_id') || `session_${Date.now()}`;
    
    this.currentSession = {
      sessionId,
      timestamp: Date.now(),
      storeId: sessionStorage.getItem('ss_current_store') || 'unknown',
      duration: 0,
      interactions: {
        searches: [],
        categories_viewed: [],
        products_viewed: [],
        cart_actions: 0,
        filters_used: []
      },
      outcome: 'ongoing',
      value: 0
    };
  }

  // Track store context for geographic attribution
  setCurrentStore(storeId: string) {
    if (this.currentSession) {
      this.currentSession.storeId = storeId;
      sessionStorage.setItem('ss_current_store', storeId);
    }
  }

  // Track search interactions
  trackSearch(searchTerm: string) {
    if (!this.currentSession) return;
    
    this.currentSession.interactions.searches.push(searchTerm);
    this.updateSessionStorage();
  }

  // Track category viewing
  trackCategoryView(category: string) {
    if (!this.currentSession) return;
    
    if (!this.currentSession.interactions.categories_viewed.includes(category)) {
      this.currentSession.interactions.categories_viewed.push(category);
    }
    this.updateSessionStorage();
  }

  // Track product viewing
  trackProductView(productId: string) {
    if (!this.currentSession) return;
    
    if (!this.currentSession.interactions.products_viewed.includes(productId)) {
      this.currentSession.interactions.products_viewed.push(productId);
    }
    this.updateSessionStorage();
  }

  // Track cart actions
  trackCartAction(value?: number) {
    if (!this.currentSession) return;
    
    this.currentSession.interactions.cart_actions += 1;
    if (value) {
      this.currentSession.value += value;
    }
    this.updateSessionStorage();
  }

  // Track filter usage
  trackFilterUse(filterType: string, filterValue: string) {
    if (!this.currentSession) return;
    
    const filterString = `${filterType}:${filterValue}`;
    if (!this.currentSession.interactions.filters_used.includes(filterString)) {
      this.currentSession.interactions.filters_used.push(filterString);
    }
    this.updateSessionStorage();
  }

  // Update session duration
  updateSessionDuration() {
    if (this.currentSession) {
      this.currentSession.duration = Date.now() - this.currentSession.timestamp;
    }
  }

  // Get customer profile data
  getCustomerProfile(): CustomerProfile {
    const sessions = this.getStoredSessions();
    const profile: CustomerProfile = {
      customerId: this.customerId,
      firstVisit: sessions.length > 0 ? Math.min(...sessions.map(s => s.timestamp)) : Date.now(),
      totalSessions: sessions.length,
      totalPurchases: sessions.filter(s => s.outcome === 'purchase').length,
      totalValue: sessions.reduce((sum, s) => sum + s.value, 0),
      preferredCategories: this.getTopCategories(sessions),
      preferredStores: this.getTopStores(sessions),
      averageSessionDuration: sessions.length > 0 ? sessions.reduce((sum, s) => sum + s.duration, 0) / sessions.length : 0,
      lastVisit: sessions.length > 0 ? Math.max(...sessions.map(s => s.timestamp)) : Date.now()
    };

    return profile;
  }

  // Determine if customer is new or returning
  isNewCustomer(): boolean {
    const sessions = this.getStoredSessions();
    return sessions.length <= 1;
  }

  // Get customer segment based on behavior
  getCustomerSegment(): 'new' | 'browser' | 'engaged' | 'loyal' | 'vip' {
    const profile = this.getCustomerProfile();
    
    if (profile.totalSessions <= 1) return 'new';
    if (profile.totalPurchases === 0 && profile.totalSessions <= 3) return 'browser';
    if (profile.totalPurchases >= 1 && profile.totalPurchases <= 2) return 'engaged';
    if (profile.totalPurchases >= 3 && profile.totalValue < 500) return 'loyal';
    return 'vip';
  }

  // Complete session with outcome
  completeSession(outcome: 'purchase' | 'abandoned', finalValue: number = 0) {
    if (!this.currentSession) return;
    
    this.updateSessionDuration();
    this.currentSession.outcome = outcome;
    this.currentSession.value = finalValue;
    
    // Store completed session
    this.storeSession(this.currentSession);
    
    // Track journey completion
    this.trackJourneyCompletion();
  }

  // Get journey analytics data for events
  getJourneyData() {
    const profile = this.getCustomerProfile();
    const segment = this.getCustomerSegment();
    
    return {
      customer_id: this.customerId,
      customer_segment: segment,
      is_new_customer: this.isNewCustomer(),
      session_number: profile.totalSessions,
      lifetime_value: profile.totalValue,
      lifetime_purchases: profile.totalPurchases,
      preferred_categories: profile.preferredCategories,
      preferred_stores: profile.preferredStores,
      days_since_first_visit: Math.floor((Date.now() - profile.firstVisit) / (1000 * 60 * 60 * 24)),
      current_session_duration: this.currentSession?.duration || 0,
      current_session_interactions: this.currentSession?.interactions || {}
    };
  }

  private getStoredSessions(): JourneySession[] {
    try {
      const stored = localStorage.getItem(`ss_customer_sessions_${this.customerId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private storeSession(session: JourneySession) {
    try {
      const sessions = this.getStoredSessions();
      sessions.push(session);
      
      // Keep only last 50 sessions to prevent storage bloat
      const recentSessions = sessions.slice(-50);
      
      localStorage.setItem(`ss_customer_sessions_${this.customerId}`, JSON.stringify(recentSessions));
    } catch (error) {
      // Could not store customer session
    }
  }

  private updateSessionStorage() {
    try {
      sessionStorage.setItem('ss_current_session', JSON.stringify(this.currentSession));
    } catch (error) {
      // Could not update session storage
    }
  }

  private getTopCategories(sessions: JourneySession[]): string[] {
    const categoryCount = new Map<string, number>();
    
    sessions.forEach(session => {
      session.interactions.categories_viewed.forEach(category => {
        categoryCount.set(category, (categoryCount.get(category) || 0) + 1);
      });
    });
    
    return Array.from(categoryCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([category]) => category);
  }

  private getTopStores(sessions: JourneySession[]): string[] {
    const storeCount = new Map<string, number>();
    
    sessions.forEach(session => {
      storeCount.set(session.storeId, (storeCount.get(session.storeId) || 0) + 1);
    });
    
    return Array.from(storeCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([store]) => store);
  }

  private trackJourneyCompletion() {
    const journeyData = this.getJourneyData();
    const sessions = this.getStoredSessions();
    
    // Track multi-session journey if this is a conversion
    if (this.currentSession?.outcome === 'purchase' && sessions.length > 1) {
      // Multi-session purchase path - tracked via the analytics service
    }
  }
}

export default CustomerJourneyService;